import React from "react";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import PersonIcon from "@mui/icons-material/Person";
import baseUrl from "../../../../api/baseUrl";
import socket from "../../../../api/socket";
import { FIELD_LABELS, statusStyles } from "./appointmentViewShared";
import PatientViewModal from "../../../patient/components/PatientViewModal";

type AppointmentMobileCardProps = {
  item: Record<string, any>;
  onOpenDetails: () => void;
  onStatusChange: () => void;
  onAddTreatment?: (patientId: string) => void;
  showAddTreatmentButton?: boolean;
  showPatientDetailsButton?: boolean;
  // Allow extra props without TS errors (some pages pass feature flags).
  [key: string]: any;
};

function FieldBlock({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide block mb-1">
        {label}
      </span>
      {children}
    </div>
  );
}

export default function AppointmentMobileCard({
  item,
  onOpenDetails,
  onStatusChange,
  onAddTreatment,
  showAddTreatmentButton = true,
  showPatientDetailsButton = false,
}: AppointmentMobileCardProps) {
  const patientName =
    item?.patientId?.fullName || item?.bookPatientId?.fullName || "N/A";
  const doctorName =
    [item?.doctorId?.firstName, item?.doctorId?.lastName]
      .filter(Boolean)
      .join(" ")
      .trim() || "—";
  const room = String(item?.roomNumber ?? "").trim() || "—";
  const queue =
    item?.queue != null && item?.queue !== ""
      ? String(Number(item.queue))
      : "—";
  const canUpdateQueue =
    String(item?.sessionName ?? "").trim() !== "" && String(item?.roomNumber ?? "").trim() !== "";
  const hasPatient = Boolean(item?.patientId?.fullName && item?.patientId?._id);
  const [patientModalOpen, setPatientModalOpen] = React.useState(false);
  const [patientLoading, setPatientLoading] = React.useState(false);
  const [patientData, setPatientData] = React.useState<any>(null);
  const [theme, setTheme] = React.useState<"light" | "dark">("light");

  React.useEffect(() => {
    const htmlTheme = document.documentElement.getAttribute("data-theme") as "light" | "dark";
    const savedTheme = (localStorage.getItem("theme") as "light" | "dark") || htmlTheme;
    if (savedTheme) setTheme(savedTheme);
  }, []);

  const handleViewPatient = async () => {
    const pid = item?.patientId?._id;
    if (!pid) return;
    setPatientModalOpen(true);
    setPatientLoading(true);
    try {
      const res = await baseUrl.get("patient/findById", { headers: { patientid: pid } });
      setPatientData(res.data?.data ?? null);
    } catch (err) {
      console.error(err);
      setPatientData(null);
    } finally {
      setPatientLoading(false);
    }
  };

  const closePatientModal = () => {
    setPatientModalOpen(false);
    setPatientData(null);
  };

  const handleAddTreatmentFromSummary = (
    patientId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    closePatientModal();
    onAddTreatment?.(patientId);
  };

  const handleUpdateQueue = async () => {
    if (!canUpdateQueue || !item?._id) return;
    const current = item?.queue != null && item?.queue !== "" ? String(item.queue) : "";
    const nextRaw = window.prompt("Enter token number", current);
    if (nextRaw == null) return;
    const n = parseInt(String(nextRaw).trim(), 10);
    if (!Number.isFinite(n) || n < 0) return;

    try {
      await baseUrl.patch(
        "/appointment/queueAppointmentById",
        { queue: n },
        { headers: { appointmentid: item._id } }
      );
    } catch (err) {
      console.error(err);
    }

    socket.emit("allAppointmentStatus", {
      appointmentid: item._id,
      status: "ongoing",
      queue: n,
      roomNumber: item?.roomNumber,
    });
    try {
      localStorage.setItem("nextQueue", String(Math.max(0, Math.floor(Number(n)))));
    } catch {
      /* ignore */
    }

    onStatusChange?.();
  };

  const handleGenerateQueue = async () => {
    if (!canUpdateQueue || !item?._id) return;
    try {
      const res = await baseUrl.patch(
        "/appointment/generateQueueAppointmentById",
        {},
        { headers: { appointmentid: item._id } }
      );
      const q = res?.data?.data?.queue;
      if (q == null) return;

      socket.emit("allAppointmentStatus", {
        appointmentid: item._id,
        status: "ongoing",
        queue: q,
        roomNumber: item?.roomNumber,
      });
      try {
        localStorage.setItem("nextQueue", String(Math.max(0, Math.floor(Number(q)))));
      } catch {
        /* ignore */
      }

      onStatusChange?.();
    } catch (err) {
      console.error(err);
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const status = e.target.value;
    if (!status || !item?._id) return;

    socket.emit("allAppointmentStatus", {
      appointmentid: item._id,
      status,
      queue: item?.queue,
      roomNumber: item?.roomNumber,
    });

    if (status === "ongoing" && item?.queue != null && item?.queue !== "") {
      try {
        localStorage.setItem(
          "nextQueue",
          String(Math.max(0, Math.floor(Number(item.queue))))
        );
      } catch {
        /* ignore */
      }
    }

    baseUrl
      .patch(
        "/appointment/statusAppointmentById",
        {},
        { headers: { appointmentid: item._id, status } }
      )
      .then(() => onStatusChange())
      .catch((err) => console.error(err));
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-600 border-l-4 border-l-indigo-400 dark:border-l-indigo-500 p-4 space-y-3 shadow-sm hover:shadow-md dark:hover:bg-slate-700/50 transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-semibold text-base text-slate-900 dark:text-slate-100 truncate flex-1 min-w-0">
          {patientName}
        </h3>
        <div className="flex gap-1.5 flex-shrink-0">
          <button
            type="button"
            onClick={onOpenDetails}
            className="w-8 h-8 rounded-lg bg-indigo-50 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-300 flex items-center justify-center hover:bg-indigo-100 dark:hover:bg-indigo-900/60 transition-colors"
            title="View appointment details"
            aria-label="View appointment details"
          >
            <RemoveRedEyeIcon sx={{ fontSize: 18 }} />
          </button>
          {hasPatient && (showPatientDetailsButton || !onAddTreatment) ? (
            <button
              type="button"
              onClick={handleViewPatient}
              className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-900/40 text-slate-700 dark:text-slate-200 flex items-center justify-center hover:bg-slate-100 dark:hover:bg-slate-900/60 transition-colors"
              title="View patient"
              aria-label="View patient"
            >
              <PersonIcon sx={{ fontSize: 18 }} />
            </button>
          ) : null}
          {showAddTreatmentButton && hasPatient && onAddTreatment ? (
            <button
              type="button"
              onClick={() => onAddTreatment(item.patientId._id)}
              className="w-8 h-8 rounded-lg bg-emerald-50 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-300 flex items-center justify-center hover:bg-emerald-100 dark:hover:bg-emerald-900/60 transition-colors"
              title="Add Treatment"
              aria-label="Add Treatment"
            >
              <MedicalInformationIcon sx={{ fontSize: 18 }} />
            </button>
          ) : null}
        </div>
      </div>

      <FieldBlock label={FIELD_LABELS.doctor}>
        <p className="text-sm text-slate-700 dark:text-slate-200">{doctorName}</p>
      </FieldBlock>

      <FieldBlock label={FIELD_LABELS.dateTime}>
        <p className="text-sm text-slate-600 dark:text-slate-300">
          {item?.date?.split("T")[0] ?? "—"}
        </p>
        <span className="mt-1 inline-block px-2.5 py-1 bg-primary/10 dark:bg-primary/30 text-primary dark:text-sky-300 rounded-md text-xs font-medium">
          {item?.startTime} - {item?.endTime}
        </span>
      </FieldBlock>

      <FieldBlock label={FIELD_LABELS.location}>
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
          {item?.sessionName || "—"}
        </p>
        <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">
          Room {room}
          <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
          <span className="tabular-nums">Token {queue}</span>
          {canUpdateQueue ? (
            <>
              <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
              <button
                type="button"
                onClick={handleUpdateQueue}
                className="underline underline-offset-2 text-primary hover:text-primary/80"
              >
                Update token
              </button>
              <span className="mx-1.5 text-slate-300 dark:text-slate-600">·</span>
              <button
                type="button"
                onClick={handleGenerateQueue}
                className="underline underline-offset-2 text-emerald-700 hover:text-emerald-700/80 dark:text-emerald-300 dark:hover:text-emerald-200"
              >
                Generate token
              </button>
            </>
          ) : null}
        </p>
      </FieldBlock>

      <div className="grid grid-cols-2 gap-3">
        <FieldBlock label={FIELD_LABELS.status}>
          <select
            onChange={handleStatusChange}
            value={item?.status ?? ""}
            className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 w-full ${
              statusStyles[item?.status] || statusStyles.default
            }`}
          >
            <option value="">Select</option>
            <option value="appointment">Upcoming</option>
            <option value="completed">Completed</option>
            <option value="canceled">Canceled</option>
            <option value="waiting">Waiting</option>
            <option value="inqueue">In Queue</option>
            <option value="ongoing">Ongoing</option>
            <option value="noshow">No Show</option>
          </select>
        </FieldBlock>
        <FieldBlock label={FIELD_LABELS.type}>
          <span
            className={`px-2.5 py-1 rounded-md text-xs font-medium inline-block capitalize ${
              item?.type === "online"
                ? "bg-primary/10 dark:bg-primary/30 text-primary"
                : "bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300"
            }`}
          >
            {item?.type || "N/A"}
          </span>
        </FieldBlock>
      </div>

      <FieldBlock label={FIELD_LABELS.purpose}>
        <p className="text-sm text-slate-700 dark:text-slate-200 font-medium">
          {item?.purposeId?.purposeName || item?.bookTreatmentId?.name || "N/A"}
        </p>
      </FieldBlock>

      <PatientViewModal
        isOpen={patientModalOpen}
        onClose={closePatientModal}
        patientData={patientData}
        loading={patientLoading}
        theme={theme}
        onAddTreatment={
          onAddTreatment && item?.patientId?._id
            ? handleAddTreatmentFromSummary
            : undefined
        }
      />
    </div>
  );
}
